///
/// EarthBrowser
///
/// Created by Matt Giger on December 16, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { logger } from '@/utils/LogDispatcher'
import { FileSystemHandleStore } from './FileSystemHandleStore'
import type {
	DirectoryPickerOptions,
	ExternalGeoPackageFile,
	FilePickerOptions,
	FileSystemDirectoryHandle,
	FileSystemFileHandle,
	FileSystemPermissionMode,
	StoredDirectoryHandle,
	StoredFileHandle
} from './types'

/**
 * Manager for File System Access API
 *
 * Handles persistent access to local file system files and directories
 * using the File System Access API. Manages permissions and stores
 * handles in IndexedDB for cross-session persistence.
 *
 * See: https://developer.mozilla.org/en-US/docs/Web/API/File_System_API
 */
export class FileSystemAccessManager {
	private store: FileSystemHandleStore
	private initialized = false

	// Cache of currently accessible handles (verified permissions)
	private handleCache = new Map<string, FileSystemFileHandle | FileSystemDirectoryHandle>()

	constructor() {
		this.store = new FileSystemHandleStore()
	}

	/**
	 * Initialize the manager
	 */
	async initialize(): Promise<void> {
		if (this.initialized) return

		await this.store.initialize()
		await this.verifyStoredHandles()
		this.initialized = true

		// logger.info('FileSystemAccessManager initialized')
	}

	/**
	 * Check if File System Access API is supported
	 */
	static isSupported(): boolean {
		return (
			typeof window !== 'undefined' &&
			'showOpenFilePicker' in window &&
			'showDirectoryPicker' in window
		)
	}

	/**
	 * Request access to a file
	 * Opens file picker and stores the handle
	 */
	async requestFileAccess(options?: FilePickerOptions): Promise<StoredFileHandle[]> {
		if (!FileSystemAccessManager.isSupported()) {
			throw new Error('File System Access API not supported')
		}

		const fileHandles = await window.showOpenFilePicker!(options)
		const storedHandles: StoredFileHandle[] = []

		// Get all existing file handles to check for duplicates
		const existingHandles = await this.store.getFileHandles()

		for (const handle of fileHandles) {
			// Check if we already have a handle for this file
			let existingHandle: StoredFileHandle | null = null
			for (const stored of existingHandles) {
				try {
					const isSame = await handle.isSameEntry(stored.handle)
					if (isSame) {
						existingHandle = stored
						break
					}
				} catch (error) {
					// Handle might be invalid, skip it
					continue
				}
			}

			if (existingHandle) {
				// Update last accessed time
				await this.store.updateLastAccessed(existingHandle.id)
				this.handleCache.set(existingHandle.id, handle)
				storedHandles.push(existingHandle)
				logger.info(`File already registered: ${handle.name} (${existingHandle.id})`)
				continue
			}

			// Check permissions
			const readPermission = await this.checkPermission(handle, 'read')
			const writePermission = await this.checkPermission(handle, 'readwrite')

			// Get file metadata
			const file = await handle.getFile()

			const id = this.generateHandleId(handle.name, 'file')
			const storedHandle: StoredFileHandle = {
				id,
				name: handle.name,
				kind: 'file',
				handle,
				lastAccessed: Date.now(),
				permissions: {
					read: readPermission,
					write: writePermission
				},
				metadata: {
					size: file.size,
					lastModified: file.lastModified,
					type: file.type
				}
			}

			await this.store.storeHandle(storedHandle)
			this.handleCache.set(id, handle)
			storedHandles.push(storedHandle)

			logger.info(`File access granted: ${handle.name} (${id})`)
		}

		return storedHandles
	}

	/**
	 * Request access to a directory
	 * Opens directory picker and stores the handle
	 */
	async requestDirectoryAccess(options?: DirectoryPickerOptions): Promise<StoredDirectoryHandle> {
		if (!FileSystemAccessManager.isSupported()) {
			throw new Error('File System Access API not supported')
		}

		const dirHandle = await window.showDirectoryPicker!(options)

		// Check permissions
		const readPermission = await this.checkPermission(dirHandle, 'read')
		const writePermission = await this.checkPermission(dirHandle, 'readwrite')

		const id = this.generateHandleId(dirHandle.name, 'directory')
		const storedHandle: StoredDirectoryHandle = {
			id,
			name: dirHandle.name,
			kind: 'directory',
			handle: dirHandle,
			lastAccessed: Date.now(),
			permissions: {
				read: readPermission,
				write: writePermission
			}
		}

		await this.store.storeHandle(storedHandle)
		this.handleCache.set(id, dirHandle)

		logger.info(`Directory access granted: ${dirHandle.name} (${id})`)

		return storedHandle
	}

	/**
	 * Get a stored file handle by ID
	 */
	async getFileHandle(id: string): Promise<FileSystemFileHandle | null> {
		// Check cache first
		const cached = this.handleCache.get(id)
		if (cached?.kind === 'file') {
			return cached
		}

		// Load from store
		const stored = await this.store.getHandle(id)
		if (stored?.kind !== 'file') {
			return null
		}

		const fileHandle = stored.handle

		// Verify permissions
		const hasPermission = await this.verifyPermission(fileHandle, 'read')
		if (!hasPermission) {
			logger.warn(`Permission denied for file: ${stored.name}`)
			return null
		}

		// Update cache and last accessed
		this.handleCache.set(id, fileHandle)
		await this.store.updateLastAccessed(id)

		return fileHandle
	}

	/**
	 * Get a stored directory handle by ID
	 */
	async getDirectoryHandle(id: string): Promise<FileSystemDirectoryHandle | null> {
		// Check cache first
		const cached = this.handleCache.get(id)
		if (cached?.kind === 'directory') {
			return cached
		}

		// Load from store
		const stored = await this.store.getHandle(id)
		if (stored?.kind !== 'directory') {
			return null
		}

		const dirHandle = stored.handle

		// Verify permissions
		const hasPermission = await this.verifyPermission(dirHandle, 'read')
		if (!hasPermission) {
			logger.warn(`Permission denied for directory: ${stored.name}`)
			return null
		}

		// Update cache and last accessed
		this.handleCache.set(id, dirHandle)
		await this.store.updateLastAccessed(id)

		return dirHandle
	}

	/**
	 * Get all stored file handles
	 */
	async getAllFileHandles(): Promise<StoredFileHandle[]> {
		return await this.store.getFileHandles()
	}

	/**
	 * Get all stored directory handles
	 */
	async getAllDirectoryHandles(): Promise<StoredDirectoryHandle[]> {
		return await this.store.getDirectoryHandles()
	}

	/**
	 * Remove a stored handle
	 */
	async removeHandle(id: string): Promise<void> {
		await this.store.deleteHandle(id)
		this.handleCache.delete(id)
		logger.info(`Removed file system handle: ${id}`)
	}

	/**
	 * Register a GeoPackage file from external file system
	 */
	async registerGeoPackageFile(handleId: string): Promise<ExternalGeoPackageFile | null> {
		const handle = await this.getFileHandle(handleId)
		if (!handle) {
			logger.error(`File handle not found: ${handleId}`)
			return null
		}

		// Verify it's a GeoPackage file
		if (!handle.name.toLowerCase().endsWith('.gpkg')) {
			logger.error(`Not a GeoPackage file: ${handle.name}`)
			return null
		}

		// Check if this handle is already registered as a GeoPackage
		const existingGeoPackages = await this.store.getGeoPackagesByHandle(handleId)
		if (existingGeoPackages.length > 0) {
			const existing = existingGeoPackages[0]
			// Update last opened time
			await this.store.updateGeoPackageLastOpened(existing.id)
			logger.info(`GeoPackage already registered: ${handle.name} (${existing.id})`)
			return existing
		}

		const file = await handle.getFile()
		const id = this.generateGeoPackageId(handle.name)

		const geopackage: ExternalGeoPackageFile = {
			id,
			name: handle.name,
			handleId,
			size: file.size,
			lastModified: file.lastModified,
			lastOpened: Date.now()
		}

		await this.store.storeGeoPackage(geopackage)
		logger.info(`Registered external GeoPackage: ${handle.name} (${id})`)

		return geopackage
	}

	/**
	 * Get all registered GeoPackage files
	 */
	async getAllGeoPackages(): Promise<ExternalGeoPackageFile[]> {
		return await this.store.getAllGeoPackages()
	}

	/**
	 * Get a GeoPackage file by ID
	 */
	async getGeoPackage(id: string): Promise<ExternalGeoPackageFile | null> {
		return await this.store.getGeoPackage(id)
	}

	/**
	 * Read a GeoPackage file data
	 */
	async readGeoPackageFile(geopackageId: string): Promise<Uint8Array | null> {
		const geopackage = await this.store.getGeoPackage(geopackageId)
		if (!geopackage) {
			logger.error(`GeoPackage not found: ${geopackageId}`)
			return null
		}

		const handle = await this.getFileHandle(geopackage.handleId)
		if (!handle) {
			logger.error(`File handle not accessible: ${geopackage.handleId}`)
			return null
		}

		const file = await handle.getFile()
		const arrayBuffer = await file.arrayBuffer()

		// Update last opened time
		await this.store.updateGeoPackageLastOpened(geopackageId)

		return new Uint8Array(arrayBuffer)
	}

	/**
	 * Write data to a GeoPackage file (if write permission granted)
	 */
	async writeGeoPackageFile(geopackageId: string, data: Uint8Array): Promise<boolean> {
		const geopackage = await this.store.getGeoPackage(geopackageId)
		if (!geopackage) {
			logger.error(`GeoPackage not found: ${geopackageId}`)
			return false
		}

		const handle = await this.getFileHandle(geopackage.handleId)
		if (!handle) {
			logger.error(`File handle not accessible: ${geopackage.handleId}`)
			return false
		}

		// Verify write permission
		const hasWritePermission = await this.verifyPermission(handle, 'readwrite')
		if (!hasWritePermission) {
			logger.error(`Write permission denied: ${handle.name}`)
			return false
		}

	try {
		const writable = await handle.createWritable()
		// Convert to Blob to ensure compatibility with FileSystemWritableFileStream
		const blob = new Blob([data.buffer as ArrayBuffer])
		await writable.write(blob)
		await writable.close()

		logger.info(`Wrote ${data.length} bytes to ${handle.name}`)
		return true
	} catch (error) {
		logger.error(`Failed to write file: ${error}`)
		return false
	}
	}

	/**
	 * Remove a GeoPackage registration
	 */
	async removeGeoPackage(id: string): Promise<void> {
		await this.store.deleteGeoPackage(id)
		logger.info(`Removed GeoPackage registration: ${id}`)
	}

	/**
	 * Check permission for a handle
	 */
	private async checkPermission(
		handle: FileSystemFileHandle | FileSystemDirectoryHandle,
		mode: FileSystemPermissionMode
	): Promise<PermissionState> {
		try {
			return await handle.queryPermission({ mode })
		} catch (error) {
			logger.error(`Failed to check permission: ${error}`)
			return 'denied'
		}
	}

	/**
	 * Verify and request permission if needed
	 */
	private async verifyPermission(
		handle: FileSystemFileHandle | FileSystemDirectoryHandle,
		mode: FileSystemPermissionMode
	): Promise<boolean> {
		const permission = await handle.queryPermission({ mode })

		if (permission === 'granted') {
			return true
		}

		if (permission === 'prompt') {
			const result = await handle.requestPermission({ mode })
			return result === 'granted'
		}

		return false
	}

	/**
	 * Verify all stored handles and clean up inaccessible ones
	 */
	private async verifyStoredHandles(): Promise<void> {
		const handles = await this.store.getAllHandles()
		const toRemove: string[] = []

		for (const stored of handles) {
			try {
				const permission = await stored.handle.queryPermission({ mode: 'read' })

				// If permission is denied (not prompt), remove it
				if (permission === 'denied') {
					toRemove.push(stored.id)
					logger.warn(`Removing inaccessible handle: ${stored.name}`)
				}
			} catch (error) {
				// Handle is invalid/stale, remove it
				toRemove.push(stored.id)
				logger.warn(`Removing invalid handle: ${stored.name}`)
			}
		}

		// Remove invalid handles
		for (const id of toRemove) {
			await this.store.deleteHandle(id)
		}

		if (toRemove.length > 0) {
			logger.info(`Cleaned up ${toRemove.length} invalid handle(s)`)
		}
	}

	/**
	 * Generate a unique ID for a handle
	 */
	private generateHandleId(name: string, kind: 'file' | 'directory'): string {
		const timestamp = Date.now()
		const random = Math.random().toString(36).substring(2, 9)
		return `${kind}_${name}_${timestamp}_${random}`
	}

	/**
	 * Generate a unique ID for a GeoPackage
	 */
	private generateGeoPackageId(name: string): string {
		const timestamp = Date.now()
		const random = Math.random().toString(36).substring(2, 9)
		return `gpkg_${name}_${timestamp}_${random}`
	}

	/**
	 * Clean up resources
	 */
	dispose(): void {
		this.handleCache.clear()
		this.store.close()
		this.initialized = false
	}
}

// Singleton instance
let managerInstance: FileSystemAccessManager | null = null

/**
 * Get the singleton instance of FileSystemAccessManager
 */
export function getFileSystemAccessManager(): FileSystemAccessManager {
	if (!managerInstance) {
		managerInstance = new FileSystemAccessManager()
	}
	return managerInstance
}

/**
 * Initialize the file system access manager
 */
export async function initializeFileSystemAccess(): Promise<void> {
	const manager = getFileSystemAccessManager()
	await manager.initialize()
}

